/**
 * **チェーンの札**（Issue #576。チェーンに積まれる `effects/chained.js` が帯の中で描く）。
 * **絵は 2D canvas の 1 つの描き方（ここ）で描き**、three はそれをテクスチャにして貼るだけ（攻撃宣言のパネルと同じ作り）。
 *
 * 札 = 鋼の枠（`chainPlate.png`。Gemini で生成して緑を抜いた）＋ 左の窓にカードのイラスト ＋ 右の暗い板に
 * 「CHAIN」と番号 ＋ **発動したプレイヤーの色の縁の線**。
 * 無効にされたリンクは**鎖で縛る**（発動の無効は X 字の太い鎖・効果の無効は効果の側に細い鎖。`BINDS`。Issue #568）。
 *
 * 並べ方（`centerColumn`）: 画面の中央に**チェーン 1 を一番上に、上から下へ**、間を開けて並べ、上下の札を**鎖のつなぎ**
 * （`chainLink.png`。`linkBox`）でつなぐ。新しい札は列の下に加わる（マスターデュエルと同じ）。**重ねない** ——
 * 札の大きさは一定で、入り切らなければ新しいものから入るだけ見せる。札ごとに少しがたつかせる（`jitterOf`。ユーザー 2026-09-28）。**盤には残さない**（帯の中だけ。常時表示の小さい札はやめた。ユーザー 2026-09-28）。
 *
 * three を import しない（純関数 ＋ 2D の context）。
 */
import chainLinkPng from './chainLink.png';
import chainPlatePng from './chainPlate.png';
import { seeded } from '../../../core/effect/motion';
import { FONT } from './common';
import { imageReady, themeImage } from './images';

/** 枠の画像の大きさ（`chainPlate.png` 900×366）と、窓（幅・高さに対する比。`scripts/hud-ornament-key.py` で測った値） */
export const PLATE = Object.freeze({ w: 900, h: 366 });
const ART = { l: 45 / 900, r: 327 / 900, t: 43 / 366, b: 324 / 366 };
const TXT = { l: 415 / 900, r: 845 / 900, t: 45 / 366, b: 310 / 366 };
/**
 * 番号の大きさ（札の canvas の px）。「CHAIN」（60px）の 2 倍ほど。札が 0.9 倍で約 106px（3 枚で約 0.6 倍 → 約 70px）
 * （**番号がデカすぎた**ので 196 → 118。ユーザー 2026-09-28）
 */
export const NUMBER_PX = 118;
/** 外周の角の切り欠き（縁の線を枠の形に沿わせる） */
const CUT = 30;

/** カードの画像のうちイラストの部分（`kit.CARD_ART` と同じ値） */
const CARD_ART = { l: 125 / 743, r: 1 - 134 / 743, t: 268 / 1080, b: 1 - 331 / 1080 };

/** 札と札の間（札の高さに対する比）。ここに鎖のつなぎが入る。**札は重ねない**（ユーザー 2026-09-28） */
export const GAP_K = 0.45;
/** 鎖のつなぎ（`chainLink.png` 300×1002。Gemini で生成して緑を抜いた）: 縦横比・札に食い込む長さ（札の高さに対する比）・横の位置（札の幅に対する比＝縦の仕切り） */
const LINK_ASPECT = 300 / 1002;
const LINK_OVER = 0.08;
const LINK_X = 371 / 900;
const LINK_FAT = 1.7;

export const PLATE_URL = chainPlatePng;
export const LINK_URL = chainLinkPng;
export const plateImage = () => themeImage(chainPlatePng);
export const linkImage = () => themeImage(chainLinkPng);

/**
 * **無効にされたリンクの札の状態**（Issue #568。ユーザー 2026-09-28）: `'negated'` = 発動の無効（`chain_negated`）/
 * `'disabled'` = 効果の無効（`chain_disabled`）/ `null` = ふつう。両方なら発動の無効。
 * どちらも**鎖で縛られ、その番の chain_solving で灰色になって沈む**（割れない。破壊は move が見せる）。違うのは鎖の感じだけ（`BINDS`）
 */
export const stateOf = (link) => (link?.negated ? 'negated' : (link?.disabled ? 'disabled' : null));

/**
 * **札に出すもの**（印の `data` にもこのまま入る）。色は描く側が入れる（places は HUD を知らない）。
 * @param link `{ chain, code, player }`
 */
export function plateSpec(link, { color = null, mirror = false } = {}) {
  return {
    chain: Number(link?.chain) || 1,
    code: link?.code ?? null,
    player: link?.player === 1 ? 1 : 0,
    color,
    mirror: Boolean(mirror),
    state: stateOf(link),
  };
}

/**
 * **鎖で縛る線**（札の座標。`{ lines: [[a, b], …], width }`。`width` は鎖の画像 1 枚の長さに対する太さの倍率）。
 * 無効にされたリンクはどちらも「鎖で縛られる」で、**鎖の感じだけ**を分ける（ユーザー 2026-09-28）:
 * - `negated`（発動の無効）: 札全体を X 字に**太い**鎖が巻き付く（重く、封じ込める）
 * - `disabled`（効果の無効）: 効果の側（イラストの窓）に**細い**鎖が 2 本、横に掛かる（軽め）
 */
export const BINDS = Object.freeze({
  negated: {
    lines: [[[-0.04, 0.05], [1.04, 0.95]], [[1.04, 0.1], [-0.04, 0.9]]].map((l) => l.map(([u, v]) => [u * PLATE.w, v * PLATE.h])),
    len: PLATE.h * 0.5,
    fat: 1.5,
  },
  disabled: {
    lines: [[[-0.02, 0.36], [0.44, 0.3]], [[-0.02, 0.7], [0.44, 0.76]]].map((l) => l.map(([u, v]) => [u * PLATE.w, v * PLATE.h])),
    len: PLATE.h * 0.3,
    fat: 1.0,
  },
});

/** 鎖で縛る（`chainLink.png` を線に沿って並べる。札の外へ少しはみ出す） */
function paintBind(ctx, state) {
  const img = linkImage();
  const b = BINDS[state];
  if (!b || !imageReady(img)) return;
  const lh = b.len;                          // 画像 1 枚の長さ（輪 3 つ）
  const lw = lh * LINK_ASPECT * b.fat;
  b.lines.forEach(([p, q]) => {
    const dx = q[0] - p[0]; const dy = q[1] - p[1];
    const n = Math.ceil(Math.hypot(dx, dy) / (lh * 0.92));
    ctx.save();
    ctx.translate(p[0], p[1]);
    ctx.rotate(Math.atan2(dy, dx) - Math.PI / 2);
    for (let i = 0; i < n; i += 1) ctx.drawImage(img, -lw / 2, i * lh * 0.92, lw, lh);
    ctx.restore();
  });
}

/** 画像の一部を箱に cover で描く */
function drawCover(ctx, img, crop, box) {
  const iw = img.naturalWidth || img.videoWidth || img.width || 0;
  const ih = img.naturalHeight || img.videoHeight || img.height || 0;
  if (!(iw > 0 && ih > 0)) return;
  const sx = iw * crop.l; const sy = ih * crop.t;
  const sw = iw * (crop.r - crop.l); const sh = ih * (crop.b - crop.t);
  const k = Math.max(box.w / sw, box.h / sh);
  const dw = sw * k; const dh = sh * k;
  ctx.save();
  ctx.beginPath();
  ctx.rect(box.x, box.y, box.w, box.h);
  ctx.clip();
  ctx.drawImage(img, sx, sy, sw, sh, box.x + (box.w - dw) / 2, box.y + (box.h - dh) / 2, dw, dh);
  ctx.restore();
}

/** 左端を x に、縦の中心を y に（入らなければ横に詰める） */
function text(ctx, str, size, x, y, color, maxW) {
  ctx.save();
  ctx.font = `900 ${size}px ${FONT}`;
  const w = ctx.measureText?.(str)?.width ?? str.length * size * 0.6;
  const sx = Math.min(1, maxW / Math.max(1, w));
  ctx.translate(x, y);
  ctx.scale(sx, 1);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.lineWidth = size * 0.14 * 2;
  ctx.strokeStyle = '#050506';
  ctx.strokeText?.(str, 0, 0);
  ctx.fillStyle = color;
  ctx.fillText(str, 0, 0);
  ctx.restore();
}

/** 外周の八角形（縁の線） */
function outline(ctx, inset) {
  const { w, h } = PLATE;
  const c = CUT;
  const i = inset;
  ctx.beginPath();
  ctx.moveTo(c + i, i); ctx.lineTo(w - c - i, i); ctx.lineTo(w - i, c + i); ctx.lineTo(w - i, h - c - i);
  ctx.lineTo(w - c - i, h - i); ctx.lineTo(c + i, h - i); ctx.lineTo(i, h - c - i); ctx.lineTo(i, c + i);
  ctx.closePath();
}

/**
 * **札を描く**（canvas の左上が原点・`PLATE` の大きさ。倍率は呼ぶ側が `ctx.scale` で掛ける）。
 * @param imgs `{ art }`（カードの画像。無ければ窓は暗いまま）
 * @param opts `{ lit, litColor }` 処理中（#568 で使う。縁の線と番号をその色にする）
 */
export function paintPlate(ctx, spec, imgs = {}, { lit = false, litColor = '#ffe45a' } = {}) {
  const { w: W, h: H } = PLATE;
  const X = (fx) => (spec.mirror ? W * (1 - fx) : W * fx);
  const xs = (a, b) => [Math.min(a, b), Math.max(a, b)];
  const [ax0, ax1] = xs(X(ART.l), X(ART.r));
  const [tx0, tx1] = xs(X(TXT.l), X(TXT.r));
  const art = { x: ax0, y: H * ART.t, w: ax1 - ax0, h: H * (ART.b - ART.t) };
  const txt = { x: tx0, y: H * TXT.t, w: tx1 - tx0, h: H * (TXT.b - TXT.t) };
  // 窓の地とイラスト（枠の下に描き、窓の八角形は枠が切る）
  ctx.fillStyle = '#0b0d12';
  ctx.fillRect(art.x - 6, art.y - 6, art.w + 12, art.h + 12);
  if (imageReady(imgs.art)) drawCover(ctx, imgs.art, CARD_ART, { x: art.x - 6, y: art.y - 6, w: art.w + 12, h: art.h + 12 });
  const frame = plateImage();
  if (imageReady(frame)) {
    ctx.save();
    if (spec.mirror) { ctx.translate(W, 0); ctx.scale(-1, 1); }
    ctx.drawImage(frame, 0, 0, W, H);
    ctx.restore();
  }
  // 発動したプレイヤーの色の縁（外周に沿った硬い線）と、番号の下の線
  const color = lit ? litColor : (spec.color ?? '#ffffff');
  ctx.lineJoin = 'miter';
  outline(ctx, 5);
  ctx.strokeStyle = color;
  ctx.lineWidth = 9;
  ctx.stroke();
  // 無効にされたリンク: 鎖で縛る（発動の無効は X 字の太い鎖、効果の無効は効果の側に細い鎖）。番号が読めるよう文字より下に
  if (spec.state) paintBind(ctx, spec.state);
  // 文字（CHAIN と番号）。板の左に寄せる（読む順）
  const pad = 34;
  const left = txt.x + pad;
  text(ctx, 'CHAIN', 60, left, txt.y + 58, '#dfe3ea', txt.w - pad * 2);
  text(ctx, String(spec.chain), NUMBER_PX, left, txt.y + txt.h * 0.6, lit ? litColor : '#ffd21a', txt.w - pad * 2 - 70);
  ctx.fillStyle = color;
  ctx.fillRect(txt.x + pad, txt.y + txt.h - 26, txt.w * 0.55, 8);
}

/** 描き直しの鍵（画像が読めたら描き直す） */
export const plateReady = (imgs = {}) => [imageReady(plateImage()), imageReady(imgs.art), imageReady(linkImage())];

/**
 * 帯の中央の列の範囲（上は HUD の紋章の下、下は画面の下の余白）と札の倍率（**一定**。3 枚が範囲に入る大きさ）
 */
export const CENTER = Object.freeze({ top: 215, bottom: 1050, scale: 0.58, mid: 0.56 });

/**
 * **帯の中央の列**（**チェーン 1 が一番上、新しい札ほど下**。間を開けて鎖でつなぐ）。札の大きさは一定（`CENTER.scale`）。
 * 入るなら列の中心を画面の高さの `CENTER.mid` あたりに置き、**入り切らないときは一番下の新しい札を範囲の下端に揃え、
 * 古い札（番号の小さい札）は範囲の上へはみ出して見えなくなる**（新しいものから入るだけ見せる。ユーザー 2026-09-28）。
 * 範囲の上端をまたぐ札は途中まで薄く（`fade`。上に続いていることが分かる）、上端より上に出た札は `fade` 0。
 * `links` を渡すと札ごとのがたつき（`jitterOf`）を足す。
 * @returns `[{ x, y, w, h, scale, rot, fade }]`（添字 0 がチェーン 1＝一番上）
 */
export function centerColumn(n, canvas = { w: 1920, h: 1080 }, links = null) {
  const k = canvas.h / 1080;
  const top = CENTER.top * k;
  const bottom = CENTER.bottom * k;
  const m = Math.max(1, n);
  const scale = CENTER.scale * k;
  const w = PLATE.w * scale;
  const h = PLATE.h * scale;
  const total = h * (m + (m - 1) * GAP_K);
  // 入るなら中央寄せ（範囲に収まるよう寄せる）、入らなければ下端に揃えて上へはみ出す
  const y0 = total <= bottom - top
    ? Math.max(top, Math.min(bottom - total, canvas.h * CENTER.mid - total / 2))
    : bottom - total;
  return Array.from({ length: m }, (_, i) => {
    const y = y0 + i * h * (1 + GAP_K);
    // がたつき（リンクを渡したときだけ。札ごとに決まった横のずれと傾き）
    const j = links?.[i] ? jitterOf(links[i]) : { dx: 0, rot: 0 };
    return {
      x: canvas.w / 2 - w / 2 + j.dx * k, y, w, h, scale, rot: j.rot, fade: Math.max(0, Math.min(1, (y + h - top) / h)),
    };
  });
}

/** 札ごとのがたつき（横のずれ px の範囲・傾き rad の範囲）。一直線より無骨に見せる（ユーザー 2026-09-28） */
export const JITTER = Object.freeze({ dx: [20, 40], rot: [2 * Math.PI / 180, 4 * Math.PI / 180] });

/**
 * **札ごとのがたつき**（`{ dx, rot }`。舞台のピクセル・rad）。**チェーンの番号とカードで決まる**（時刻・帯に依らない ＝
 * 同じ札は前の帯でも次の帯でも同じずれ）。向きは左右交互を基本に、大きさは乱数。
 */
export function jitterOf(link) {
  const chain = Number(link?.chain) || 1;
  const r = seeded(`chain:${chain}:${link?.code ?? ''}`);
  const lerp = ([a, b], u) => a + (b - a) * u;
  const sx = chain % 2 === 1 ? -1 : 1;
  const sr = r() < 0.5 ? -1 : 1;
  return { dx: sx * lerp(JITTER.dx, r()), rot: sr * lerp(JITTER.rot, r()) };
}

/**
 * **鎖のつなぎの箱**（札の座標。札の左上が原点・`PLATE` の大きさの単位）。札の**下**に付けて、下の札（次の番号）とつなぐ。
 * 札の中の縦の仕切り（イラストの窓と番号の板の間）の真下から、下の札に同じだけ食い込むところまで。
 */
export function linkBox() {
  const h = PLATE.h * (GAP_K + 2 * LINK_OVER);
  // 画像の縦横比のままだと細くて見えないので、横に太らせる（鎖の輪が太く見える程度）
  const w = h * LINK_ASPECT * LINK_FAT;
  const cx = PLATE.w * LINK_X;
  return { x: cx - w / 2, y: PLATE.h * (1 - LINK_OVER), w, h };
}
